import { useEffect, useState } from 'react'
import type { ChatDeck, ChatImage } from './api'

/**
 * The chat's queued follow-ups — prompts typed while the agent works, run in order once it is free
 * (AB#288) — kept per bid so they outlive ChatPanel: App.tsx only mounts the panel on the editor
 * and dashboard views with the chat open, so a trip to Home, Knowledge, Bid settings, Account
 * settings or Releases, or collapsing the chat, unmounts it and used to throw the queue away while
 * the running job itself survived.
 *
 * Keyed by bid because the chat is: a prompt queued in bid A must never show up in, or be sent to,
 * bid B. Popping stays in ChatPanel — while it is unmounted nothing pops, and routing happens at
 * pop time against the part open then.
 *
 * In memory only, like chat-draft.ts — module-level, lost on reload or app restart, never written
 * to localStorage/sessionStorage or sent to a server: a prompt can hold confidential bid content.
 * Same module-level-store + subscribe shape as that file.
 */
export interface QueuedPrompt {
  id: string
  text: string
  images: ChatImage[]
  /** The staged PowerPoint decks of the message (AB#452); absent when none. */
  decks?: ChatDeck[]
}

const EMPTY: QueuedPrompt[] = []

/** Replaced on every write, never mutated, so a component holding it in state re-renders. */
let queues: ReadonlyMap<string, QueuedPrompt[]> = new Map()
const listeners = new Set<(q: ReadonlyMap<string, QueuedPrompt[]>) => void>()

/** "No bid selected" is a slot of its own, never shared with a real bid. */
const keyOf = (bid: string | null): string => bid ?? ''

function emit(): void {
  for (const l of listeners) l(queues)
}

/** Imperative snapshot of `bid`'s queue, oldest first (an empty list when there is none). */
export function getChatQueue(bid: string | null): QueuedPrompt[] {
  return queues.get(keyOf(bid)) ?? EMPTY
}

/** Set `bid`'s queue, as a list or as an update of the stored one. The update reads the store, not
 *  a component's closure, so two writes in one tick both land. An emptied queue is forgotten. */
export function setChatQueue(
  bid: string | null,
  update: QueuedPrompt[] | ((prev: QueuedPrompt[]) => QueuedPrompt[]),
): void {
  const next = typeof update === 'function' ? update(getChatQueue(bid)) : update
  const copy = new Map(queues)
  if (next.length === 0) copy.delete(keyOf(bid))
  else copy.set(keyOf(bid), next)
  queues = copy
  emit()
}

/** Forget every queue. For tests: the store is module-level, so it outlives a single test. */
export function resetChatQueues(): void {
  queues = new Map()
  emit()
}

/** Reactive view of `bid`'s queue. Follows `bid`: when it changes, the new bid's queue is returned
 *  from that very render, never the previous bid's. */
export function useChatQueue(bid: string | null): QueuedPrompt[] {
  const [state, setState] = useState(queues)
  useEffect(() => {
    const l = (q: ReadonlyMap<string, QueuedPrompt[]>): void => setState(q)
    listeners.add(l)
    l(queues)
    return () => {
      listeners.delete(l)
    }
  }, [])
  return state.get(keyOf(bid)) ?? EMPTY
}
